<template>
  <span class="shipping-option__name">
    <strong>{{ name }}</strong>
  </span>
  <p v-if="false" class="shipping-option__info">
    <template v-if="orderCutOff">
      Order within <i>{{ cutOffCountdown }}</i> to receive
    </template>
    <template v-else>Order now to receive </template>
    <b>{{ estimatedDeliveryDateMin }}</b> -
    <b>{{ estimatedDeliveryDateMax }}</b>
  </p>
</template>

<script>
import format from "date-fns/format";
import { mapGetters, mapState } from "vuex";
import { orderCutOffForNextDay } from "@/config.js";
import { zonedTimeToUtc } from "date-fns-tz";
import {
  intervalToDuration,
  isAfter,
  addDays,
  differenceInHours,
} from "date-fns";

export default {
  name: "ShippingOption",
  props: {
    rate: {
      type: Object,
      required: true,
    },
    weight: {
      type: Number,
      default: 0.2,
    },
  },
  data() {
    return {
      estimatedDeliveryDateMin: null,
      estimatedDeliveryDateMax: null,
      tick: null,
      now: new Date(),
    };
  },
  computed: {
    ...mapState("app", ["userCountry"]),
    ...mapGetters("app", ["userPostCode", "currencyCode"]),
    postcode() {
      if (this.userPostCode) {
        return this.userPostCode;
      }

      return this.usersEstimatedPostCode;
    },
    name() {
      const isFree = this.rate.price === "0.00";
      const maxCondition = this.rate.max_order_subtotal
        ? this.$filters.toCurrency(
            this.rate.max_order_subtotal,
            this.currencyCode
          )
        : null;
      const minCondition = this.rate.min_order_subtotal
        ? this.$filters.toCurrency(
            this.rate.min_order_subtotal,
            this.currencyCode
          )
        : null;
      const ratePrice = this.$filters.toCurrency(
        this.rate.price,
        this.currencyCode
      );

      const freeConditions = {
        all: `FREE ${this.rate.name} on all orders`,
        min: `FREE ${this.rate.name} on orders over ${minCondition}`,
        max: `FREE ${this.rate.name} on orders up to ${maxCondition}`,
        both: `FREE ${this.rate.name} on orders between ${minCondition} and ${maxCondition}`,
      };

      const paidConditions = {
        all: `${ratePrice} via ${this.rate.name} on all orders`,
        min: `${ratePrice} via ${this.rate.name} on orders over ${minCondition}`,
        max: `${ratePrice} via ${this.rate.name} on orders up to ${maxCondition}`,
        both: `${ratePrice} via ${this.rate.name} on orders between ${minCondition} and ${maxCondition}`,
      };

      const selectedConditions = isFree ? freeConditions : paidConditions;

      if (!minCondition && !maxCondition) {
        return selectedConditions["all"];
      } else if (minCondition && maxCondition) {
        return selectedConditions["both"];
      } else if (minCondition) {
        return selectedConditions["min"];
      } else if (maxCondition) {
        return selectedConditions["max"];
      } else {
        return selectedConditions["max"];
      }
    },
    orderCutOff() {
      if (!this.currencyCode) {
        return;
      }

      const rule = orderCutOffForNextDay[this.currencyCode];
      const time = rule.time;

      const todayAtCutOff = new Date();
      todayAtCutOff.setHours(...time.split(":"));

      const cutOffToday = zonedTimeToUtc(todayAtCutOff, rule.timezone);
      const cutOffPassed = isAfter(new Date(), cutOffToday);

      const actualCutOffDate = cutOffPassed
        ? addDays(cutOffToday, 1)
        : cutOffToday;

      const duration = intervalToDuration({
        start: this.now,
        end: actualCutOffDate,
      });

      const diff = differenceInHours(actualCutOffDate, new Date());

      if (diff >= 4) {
        return false;
      }

      return duration;
    },
    cutOffCountdown() {
      const hours = this.orderCutOff.hours;
      const mins = this.orderCutOff.minutes;
      const secs = this.orderCutOff.seconds;

      if (hours > 0) {
        return `${hours} hrs, ${mins} mins, ${secs} secs`;
      }

      if (mins > 0) {
        return `${mins} mins, ${secs} secs`;
      }

      return `${secs} secs`;
    },
  },
  async mounted() {
    await this.getUsersPostCode();
    // this.getEstimatedDeliveryDate();

    this.tick = setInterval(() => {
      this.now = new Date();
    }, 1000);
  },
  methods: {
    async getUsersPostCode() {
      if (this.userPostCode) {
        return;
      }

      const response = await fetch(
        "https://pro.ip-api.com/json?key=Y6SLxPFTt7gdeOe"
      );
      const { zip: usersEstimatedPostCode } = await response.json();

      this.usersEstimatedPostCode = usersEstimatedPostCode;
    },
    async getEstimatedDeliveryDate() {
      if (!this.postcode) {
        return;
      }

      if (!this.rate.name) {
        return;
      }

      const requestBody = {
        postcode: this.postcode,
        country: this.userCountry,
        weight: this.weight,
        service: this.rate.name,
      };

      const response = await fetch(
        process.env.RATANDBOA_NODE_APP_URL + "/delivery-estimate",
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      const json = await response.json();

      const { estimated_delivery_date_min, estimated_delivery_date_max } = json;

      this.estimatedDeliveryDateMin = format(
        new Date(estimated_delivery_date_min),
        "E do MMM"
      );
      this.estimatedDeliveryDateMax = format(
        new Date(estimated_delivery_date_max),
        "E do MMM"
      );
    },
  },
};
</script>
